import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ModelType, IMusicGeneration, PaginationResult,
  MusicGenerationQuery, PaginationQuery, TaskStatus,
  CreateMusicGenerationRequest
} from '@packages/global';
import { musicApi } from '../services/user/music.api';
import { useModels } from '../hooks/useModels';
import Pagination from '../components/Pagination';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType } from '../services/websocket/types';
import { MagnifyingGlassIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import MusicTabs from '../components/common/MusicTabs';
import MusicGenerationForm, { MusicFormData } from '../components/MusicGeneration/MusicGenerationForm';
import MusicCard from '../components/MusicGeneration/MusicCard';

const DEFAULT_FORM: MusicFormData = {
  prompt: '',
  lyrics: '',
  duration: 90,
  modelId: '',
  generationMode: 'simple',
};

export default function MusicGenerationsPage() {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const [items, setItems] = useState<IMusicGeneration[]>([]);
  const { models, loading: modelsLoading } = useModels(ModelType.TEXT_TO_MUSIC);
  const [loading, setLoading] = useState(true);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();

  const [formData, setFormData] = useState<MusicFormData>(DEFAULT_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1, limit: 24, total: 0, totalPages: 0,
    sortBy: 'createdAt', sortOrder: 'desc'
  });

  const loadItems = async (
    queryParams: Partial<MusicGenerationQuery> = {},
    page = 1,
    limit = 24
  ) => {
    try {
      const queryData: PaginationQuery<MusicGenerationQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          ...queryParams,
          musicType: 'generation',
        },
        pagination: { page, limit, sortBy: 'createdAt', sortOrder: 'desc' }
      };
      const response = await musicApi.musicGenerationPageList(queryData);
      setItems(response.items);
      setPagination(prev => ({
        ...prev, page, limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载音乐生成任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  useEffect(() => {
    if (!modelsLoading && !formData.modelId && models.length > 0) {
      setFormData(prev => ({ ...prev, modelId: models[0].id }));
    }
  }, [models, modelsLoading, formData.modelId]);

  // WebSocket 订阅
  useEffect(() => {
    if (!client || !isConnected) return;
    const unsubStatus = client.onMessage(MessageType.TASK_STATUS_CHANGED, (msg: TaskStatusChangedMessage) => {
      if (msg.taskType !== TaskType.MUSIC_GENERATION) return;
      setItems(prev => {
        const idx = prev.findIndex(i => i.id === msg.taskId);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], status: msg.newStatus };
        if (msg.newStatus === TaskStatus.COMPLETED || msg.newStatus === TaskStatus.FAILED) {
          musicApi.getMusicGeneration(msg.taskId)
            .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
            .catch(console.error);
        }
        return next;
      });
    });

    const unsubCompleted = client.onMessage(MessageType.TASK_COMPLETED, (msg: any) => {
      if (msg.taskType !== TaskType.MUSIC_GENERATION) return;
      musicApi.getMusicGeneration(msg.taskId)
        .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
        .catch(console.error);
    });

    const unsubFailed = client.onMessage(MessageType.TASK_FAILED, (msg: any) => {
      if (msg.taskType !== TaskType.MUSIC_GENERATION) return;
      musicApi.getMusicGeneration(msg.taskId)
        .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
        .catch(console.error);
    });

    return () => { unsubStatus(); unsubCompleted(); unsubFailed(); };
  }, [client, isConnected]);

  /**
   * 从 prompt / lyrics 自动截取标题（最多20字）
   */
  const inferTitle = (prompt: string, lyrics?: string): string => {
    // 优先取歌词第一行非空内容
    if (lyrics) {
      const firstLine = lyrics.split('\n').map(l => l.trim()).find(l => l && !l.startsWith('['));
      if (firstLine) return firstLine.slice(0, 20);
    }
    return prompt.slice(0, 20);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.prompt || !formData.modelId) {
      await alert(t('generation:music.alertFillPrompt'), t('generation:music.alertHint'), 'warning');
      return;
    }
    try {
      const requestData: CreateMusicGenerationRequest = {
        prompt: formData.prompt,
        duration: formData.duration,
        modelId: formData.modelId,
        generationMode: formData.generationMode,
        lyrics: formData.lyrics || undefined,
        bpm: formData.bpm,
        language: formData.language,
        keyscale: formData.keyscale,
        timesignature: formData.timesignature,
        referenceAudioUrl: formData.referenceAudioUrl,
      };
      const newItem = await musicApi.createMusicGeneration(requestData);

      // 自动填充标题
      const autoTitle = inferTitle(formData.prompt, formData.lyrics);
      if (autoTitle) {
        musicApi.updateMusicGenerationTitle(newItem.id, autoTitle).catch(console.error);
        newItem.title = autoTitle;
      }

      setItems(prev => [newItem, ...prev]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
      // 重置内容，保留模式和模型
      setFormData(prev => ({
        ...DEFAULT_FORM,
        modelId: prev.modelId,
        generationMode: prev.generationMode,
      }));
    } catch (error: any) {
      await alert(error.response?.data?.message || t('generation:music.createFailed'), t('generation:music.alertError'), 'danger');
    }
  };

  const handleReuseParams = (item: IMusicGeneration) => {
    setFormData({
      prompt: item.prompt,
      lyrics: item.lyrics || '',
      duration: item.duration,
      modelId: item.modelId,
      generationMode: item.generationMode ?? 'simple',
      bpm: item.bpm,
      language: item.language,
      keyscale: item.keyscale,
      timesignature: item.timesignature,
      referenceAudioUrl: item.referenceAudioUrl,
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('generation:music.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (!confirmed) return;
    try {
      await musicApi.deleteMusicGeneration(id);
      loadItems({}, pagination.page, pagination.limit);
    } catch (error) {
      console.error('删除失败:', error);
    }
  };

  const handleDownload = async (item: IMusicGeneration) => {
    if (!item.resultAudioUrl) return;
    try {
      const response = await fetch(item.resultAudioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `music-${item.id}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle shadow-xl flex flex-col">
        <MusicTabs />
        <MusicGenerationForm
          formData={formData}
          models={models}
          modelsLoading={modelsLoading}
          onFormDataChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
          onSubmit={handleSubmit}
        />
      </div>

      {/* 右侧内容 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 标题 + 搜索 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('generation:music.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('generation:music.subtitle')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('generation:music.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    loadItems({ search: e.target.value, status: statusFilter || undefined }, 1);
                  }}
                  className="block w-full pl-9 pr-3 py-2 border border-border-subtle rounded-lg bg-bg-form placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-gray-50 text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  const v = e.target.value as TaskStatus | '';
                  setStatusFilter(v);
                  loadItems({ search: searchTerm, status: v || undefined }, 1);
                }}
                className="px-3 py-2 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="">{t('generation:music.allStatus')}</option>
                {Object.values(TaskStatus).map(s => (
                  <option key={s} value={s}>{t(`generation:status.${s}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 列表 */}
          {loading ? (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-400">{t('common:loading')}</span>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 text-center">
              <MusicalNoteIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">{t('generation:music.empty')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('generation:music.emptyHint')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map(item => (
                <MusicCard
                  key={item.id}
                  item={item}
                  modelName={models.find(m => m.id === item.modelId)?.title || item.modelId}
                  onReuse={handleReuseParams}
                  onDelete={handleDelete}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
